import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../api/client';
import type { StackActivity, StackActivityEntry, StackActivityKind } from '../api/types';

export const STACK_ACTIVITY_QUERY_KEY = ['stack-activity'] as const;

/**
 * Stack builds still queued or running, across every database. Jobs live on
 * the server, so this survives navigation, panel unmount, and page reload.
 */
export function useStackActivity() {
  const query = useQuery<StackActivity>({
    queryKey: STACK_ACTIVITY_QUERY_KEY,
    queryFn: () => apiClient.getStackActivity(),
    // Fast only while a build runs or waits in the line. Automatic work
    // waiting to resume can wait for hours, so it is looked at slowly, as
    // when nothing runs. Starting work here invalidates this query, so the
    // slow poll exists to notice jobs another client began — a few seconds'
    // lag is fine, and the endpoint is an in-memory list.
    refetchInterval: (query) => (query.state.data?.active.length ? 1000 : 5_000),
    refetchIntervalInBackground: false,
  });
  return {
    active: query.data?.active ?? [],
    isRunning: (query.data?.active.length ?? 0) > 0,
    /** Automatic refreshes; those `waiting` are due and wait for the worker. */
    scheduled: query.data?.scheduled ?? [],
    waitingReapplies: query.data?.waiting_reapplies ?? [],
  };
}


/**
 * The build a panel should re-attach to when it mounts without a job of its
 * own. Running work wins over queued work; ties go to the oldest job.
 */
export function adoptableStackJob(
  active: StackActivityEntry[],
  kind: StackActivityKind,
  dbId: string,
  projectId: number
): StackActivityEntry | undefined {
  const mine = active.filter(
    (entry) =>
      entry.kind === kind && entry.database_id === dbId && entry.project_id === projectId
  );
  return mine.find((entry) => entry.state === 'running') ?? mine[0];
}
